/**
 * ModelosOrcamentoTab — A7 (V2-COMERCIAL-ACESSO).
 *
 * Aba "Modelos de orçamento" dentro de Configurações › Comercial — CRM.
 * Reproduz o mockup `docs/architecture/mockup-modelos-orcamento.html`:
 *  - LISTA: grid 2 colunas de cards (nome, chip do tipo OU "Sem tipo
 *    vinculado", "N blocos", "N itens sugeridos", rodapé com os nomes dos
 *    blocos + "Editar") e o card tracejado "+ Novo modelo".
 *  - EDITOR: drawer à direita (~600px) com "Nome do modelo" + "Tipo de projeto
 *    (opcional)", blocos com nome editável inline, badge "repete por Setor
 *    1..N", "Remover bloco", itens sugeridos com "×" e "+ Item sugerido",
 *    botão tracejado "+ Adicionar bloco" e rodapé "Cancelar" / "Salvar modelo".
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Plus, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import {
  Badge,
  Button,
  EmptyState,
  HelpTip,
  Input,
  Label,
  Select,
  SelectOption,
  useConfirmDialog,
  useToast,
} from '@/components/ui';
import { api } from '@/lib/api';
import { errMsg } from '@/lib/api-error';
import { useMyFeatureFlags } from '@/lib/feature-flags-store';
import { flattenTiposComSubtipos, useProjetoTipos } from '@/lib/projetos-store';
import {
  useModelos,
  useModeloBlocos,
  useModelosActions,
  useModeloBlocoActions,
  type ModeloBloco,
  type OrcamentoModeloResumo,
  type TipoBloco,
} from '@/lib/orcamento-modelos-store';

interface TipoOpcao {
  id: string;
  label: string;
}

export function ModelosOrcamentoTab() {
  // H6 (padrão A8): flags em voo → "Carregando…" — sem flash do EmptyState
  // "indisponível" enquanto a query de flags não respondeu.
  const flagsQuery = useMyFeatureFlags();
  const orcamentoOn = flagsQuery.data?.flags?.ORCAMENTO_ENABLED ?? false;
  const modelosQuery = useModelos();
  const modelos = modelosQuery.data ?? [];
  const tiposQuery = useProjetoTipos();
  const tipoOpcoes = useMemo(() => flattenTiposComSubtipos(tiposQuery.data?.items ?? []), [tiposQuery.data]);
  const [drawer, setDrawer] = useState<{ modo: 'novo' } | { modo: 'editar'; modelo: OrcamentoModeloResumo } | null>(
    null,
  );

  if (flagsQuery.isLoading) {
    return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>;
  }
  if (!orcamentoOn) {
    return (
      <EmptyState
        title="Ferramenta de Orçamento indisponível"
        description="A Ferramenta de Orçamento ainda não está ativada para a sua conta."
      />
    );
  }

  return (
    <div>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-xl tracking-tight text-ink-1 dark:text-ink-dark-1 inline-flex items-center gap-1.5">
            Modelos de orçamento
            <HelpTip texto="Modelo é um esqueleto de orçamento: blocos e itens sugeridos que já vêm prontos quando o vendedor clica em Aplicar modelo. Poupa digitação e padroniza." />
          </h2>
          <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-1 max-w-2xl">
            Modelos prontos de orçamento por tipo de projeto — blocos (ex.: Rack, Infra, Setor) e os itens sugeridos de
            cada bloco. No orçamento, &ldquo;Aplicar modelo&rdquo; já traz os blocos e itens.
          </p>
        </div>
        <Button size="sm" onClick={() => setDrawer({ modo: 'novo' })} data-testid="btn-novo-modelo">
          <span className="inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" />
            Novo modelo
          </span>
        </Button>
      </div>

      {modelosQuery.isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando modelos…</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3.5">
          {modelos.map((m) => (
            <ModeloCard key={m.id} modelo={m} onEditar={() => setDrawer({ modo: 'editar', modelo: m })} />
          ))}
          <button
            type="button"
            onClick={() => setDrawer({ modo: 'novo' })}
            className="min-h-[120px] rounded-xl border-[1.5px] border-dashed border-line dark:border-line-dark flex flex-col items-center justify-center gap-2 text-ink-3 dark:text-ink-dark-3 font-semibold hover:border-red hover:text-red transition-colors"
            data-testid="card-novo-modelo"
          >
            <Plus className="w-5 h-5" />
            Novo modelo
          </button>
        </div>
      )}

      {drawer?.modo === 'novo' && <NovoModeloDrawer tipoOpcoes={tipoOpcoes} onClose={() => setDrawer(null)} />}
      {drawer?.modo === 'editar' && (
        <EditarModeloDrawer modelo={drawer.modelo} tipoOpcoes={tipoOpcoes} onClose={() => setDrawer(null)} />
      )}
    </div>
  );
}

// ─── Card da lista ─────────────────────────────────────────────────────

function ModeloCard({ modelo, onEditar }: { modelo: OrcamentoModeloResumo; onEditar: () => void }) {
  return (
    <button
      type="button"
      onClick={onEditar}
      className="text-left bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-xl p-4 shadow-card hover:shadow-card-hover hover:-translate-y-0.5 transition-all"
      data-testid={`modelo-card-${modelo.id}`}
    >
      <p className="font-semibold text-lg text-ink-1 dark:text-ink-dark-1">{modelo.nome}</p>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {modelo.tipoNome ? (
          <Badge variant="info" size="sm">
            {modelo.tipoNome}
          </Badge>
        ) : (
          <Badge variant="neutral" size="sm">
            Sem tipo vinculado
          </Badge>
        )}
        <Badge variant="neutral" size="sm">
          {modelo.blocosCount} {modelo.blocosCount === 1 ? 'bloco' : 'blocos'}
        </Badge>
        <Badge variant="neutral" size="sm">
          {modelo.itensCount} {modelo.itensCount === 1 ? 'item sugerido' : 'itens sugeridos'}
        </Badge>
      </div>
      <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-line-soft dark:border-line-soft-dark">
        <span className="text-xs text-ink-3 dark:text-ink-dark-3 font-medium truncate">
          {modelo.blocoNomes.length > 0 ? modelo.blocoNomes.join(' · ') : 'Sem blocos ainda'}
        </span>
        <span className="text-xs font-bold text-red dark:text-red-glow shrink-0">Editar</span>
      </div>
    </button>
  );
}

// ─── Drawer (casca compartilhada) ─────────────────────────────────────

function DrawerShell({
  titulo,
  onClose,
  footer,
  children,
}: {
  titulo: string;
  onClose: () => void;
  footer: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onEsc);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 animate-fade-in" role="dialog" aria-modal="true" aria-label={titulo}>
      <div
        className="absolute inset-0 bg-ink-1/40 dark:bg-ink-dark-1/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="absolute top-0 right-0 h-full w-full max-w-[600px] bg-paper dark:bg-paper-dark shadow-card-hover flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 bg-paper-surface dark:bg-paper-dark-surface border-b border-line dark:border-line-dark">
          <div>
            <h2 className="font-display text-xl uppercase tracking-tight text-ink-1 dark:text-ink-dark-1">{titulo}</h2>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-0.5">
              Aplicado no orçamento via &ldquo;Aplicar modelo&rdquo;
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="p-2 rounded-lg bg-paper-warm dark:bg-paper-dark-warm text-ink-3 dark:text-ink-dark-3 hover:text-red transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        <div className="flex items-center gap-2 px-5 py-3 border-t border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface">
          {footer}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Campos Nome + Tipo (linha dupla do topo do drawer). */
function CamposNomeTipo({
  nome,
  setNome,
  tipoId,
  setTipoId,
  tipoOpcoes,
}: {
  nome: string;
  setNome: (v: string) => void;
  tipoId: string;
  setTipoId: (v: string) => void;
  tipoOpcoes: TipoOpcao[];
}) {
  return (
    <div className="grid sm:grid-cols-2 gap-3 mb-4">
      <div>
        <Label htmlFor="modelo-nome">Nome do modelo</Label>
        <Input
          id="modelo-nome"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ex.: CFTV padrão"
          data-testid="input-nome-modelo"
        />
      </div>
      <div>
        <span className="flex items-center gap-1.5 mb-1.5">
          <Label htmlFor="modelo-tipo" className="mb-0">Tipo de projeto (opcional)</Label>
          <HelpTip texto="Com vínculo, o modelo aparece em destaque quando o orçamento é daquele tipo de projeto. Sem vínculo, fica disponível pra todos." />
        </span>
        <Select id="modelo-tipo" value={tipoId} onChange={(e) => setTipoId(e.target.value)} data-testid="select-tipo-modelo">
          <SelectOption value="">— sem vínculo —</SelectOption>
          {tipoOpcoes.map((o) => (
            <SelectOption key={o.id} value={o.id}>
              {o.label}
            </SelectOption>
          ))}
        </Select>
      </div>
    </div>
  );
}

function TituloBlocos() {
  return (
    <p className="text-xs font-bold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3 mb-2.5 mt-1 inline-flex items-center gap-1.5">
      Blocos do modelo
      <HelpTip texto="Bloco é um agrupamento de itens dentro do orçamento (ex.: Rack, Infra, Setor). Os itens sugeridos são só lembretes — o vendedor preenche preço e quantidade depois." />
    </p>
  );
}

const BADGE_REPETIVEL = 'repete por Setor 1..N';

/** Formulário inline do "+ Adicionar bloco" (nome + Fixo/Repetível). */
function AddBlocoForm({ onAdd, pending }: { onAdd: (nome: string, tipoBloco: TipoBloco) => void; pending?: boolean }) {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');
  const [tipoBloco, setTipoBloco] = useState<TipoBloco>('fixo');

  function confirmar() {
    const n = nome.trim();
    if (!n) return;
    onAdd(n, tipoBloco);
    setNome('');
    setTipoBloco('fixo');
    setAberto(false);
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="w-full rounded-xl border-[1.5px] border-dashed border-line dark:border-line-dark py-2.5 text-sm font-semibold text-ink-3 dark:text-ink-dark-3 hover:border-red hover:text-red transition-colors"
        data-testid="btn-adicionar-bloco"
      >
        + Adicionar bloco
      </button>
    );
  }
  return (
    <div className="rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface p-3 flex flex-wrap items-end gap-2">
      <div className="flex-1 min-w-[140px]">
        <Label htmlFor="novo-bloco-nome">Nome do bloco</Label>
        <Input
          id="novo-bloco-nome"
          size="sm"
          autoFocus
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && confirmar()}
          placeholder="Ex.: Rack, Infra, Setor"
          data-testid="input-novo-bloco"
        />
      </div>
      <div className="w-36">
        <span className="flex items-center gap-1.5 mb-1.5">
          <Label htmlFor="novo-bloco-repeticao" className="mb-0">Repetição</Label>
          <HelpTip texto="Fixo entra uma vez só no orçamento. Repetível pode ser duplicado (ex.: um bloco por setor do prédio)." />
        </span>
        <Select
          id="novo-bloco-repeticao"
          size="sm"
          value={tipoBloco}
          onChange={(e) => setTipoBloco(e.target.value as TipoBloco)}
        >
          <SelectOption value="fixo">Fixo (1×)</SelectOption>
          <SelectOption value="repetivel">Repetível (+)</SelectOption>
        </Select>
      </div>
      <Button size="sm" onClick={confirmar} disabled={!nome.trim() || pending} data-testid="btn-confirmar-bloco">
        Adicionar
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setAberto(false)}>
        Cancelar
      </Button>
    </div>
  );
}

/** Link + input inline do "+ Item sugerido". */
function AddItemInline({ onAdd }: { onAdd: (nome: string) => void }) {
  const [aberto, setAberto] = useState(false);
  const [nome, setNome] = useState('');

  function confirmar() {
    const n = nome.trim();
    if (!n) return;
    onAdd(n);
    setNome('');
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="text-xs font-bold text-red dark:text-red-glow hover:text-red-deep mt-1.5"
      >
        + Item sugerido
      </button>
    );
  }
  return (
    <div className="flex items-center gap-2 mt-2">
      <Input
        size="sm"
        autoFocus
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') confirmar();
          if (e.key === 'Escape') setAberto(false);
        }}
        placeholder="Nome do item (só o lembrete)"
        wrapperClassName="flex-1"
      />
      <Button size="sm" variant="secondary" onClick={confirmar} disabled={!nome.trim()}>
        Adicionar
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setAberto(false)}>
        Fechar
      </Button>
    </div>
  );
}

/** Linha de item sugerido (pontinho + nome + ×), como no mockup. */
function ItemLinha({ nome, onRemove }: { nome: string; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2.5 py-1.5 border-b border-line-soft dark:border-line-soft-dark last:border-b-0 text-sm text-ink-1 dark:text-ink-dark-1">
      <span className="w-1.5 h-1.5 rounded-full bg-ink-3 dark:bg-ink-dark-3 shrink-0" />
      <span className="flex-1 truncate">{nome}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remover ${nome}`}
        className="text-ink-3 dark:text-ink-dark-3 hover:text-red px-1 shrink-0"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Drawer: NOVO modelo (estado local, salva tudo no fim) ────────────

interface BlocoLocal {
  key: number;
  nome: string;
  tipoBloco: TipoBloco;
  itens: string[];
}

function NovoModeloDrawer({ tipoOpcoes, onClose }: { tipoOpcoes: TipoOpcao[]; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const { createModelo } = useModelosActions();
  const [nome, setNome] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [blocos, setBlocos] = useState<BlocoLocal[]>([]);
  const [salvando, setSalvando] = useState(false);

  function patchBloco(key: number, patch: Partial<BlocoLocal>) {
    setBlocos((prev) => prev.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  }

  async function salvar() {
    if (!nome.trim() || salvando) return;
    setSalvando(true);
    try {
      const { modelo } = await createModelo.mutateAsync({ nome: nome.trim(), tipoId: tipoId || null });
      for (const b of blocos) {
        if (!b.nome.trim()) continue;
        const resp = await api.post<{ bloco: { id: string } }>(
          `/api/v1/orcamento-modelos/modelos/${modelo.id}/blocos`,
          { nome: b.nome.trim(), tipoBloco: b.tipoBloco },
        );
        for (const item of b.itens) {
          await api.post(`/api/v1/orcamento-modelos/blocos/${resp.bloco.id}/itens`, { nome: item });
        }
      }
      await qc.invalidateQueries({ queryKey: ['orcamento-modelos'] });
      toast.show('Modelo criado.');
      onClose();
    } catch (e) {
      toast.show(errMsg(e) ?? 'Não foi possível salvar o modelo.', { variant: 'error' });
      setSalvando(false);
    }
  }

  return (
    <DrawerShell
      titulo="Novo modelo"
      onClose={onClose}
      footer={
        <>
          <span className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={!nome.trim() || salvando} data-testid="btn-salvar-modelo">
            {salvando ? 'Salvando…' : 'Salvar modelo'}
          </Button>
        </>
      }
    >
      <CamposNomeTipo nome={nome} setNome={setNome} tipoId={tipoId} setTipoId={setTipoId} tipoOpcoes={tipoOpcoes} />
      <TituloBlocos />
      <div className="space-y-3 mb-3">
        {blocos.map((b) => (
          <div
            key={b.key}
            className="rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface p-3.5"
          >
            <div className="flex items-center gap-2 mb-1.5">
              <input
                value={b.nome}
                onChange={(e) => patchBloco(b.key, { nome: e.target.value })}
                aria-label="Nome do bloco"
                className="flex-1 min-w-0 bg-transparent font-semibold text-base text-ink-1 dark:text-ink-dark-1 focus:outline-none focus:border-b focus:border-red"
              />
              {b.tipoBloco === 'repetivel' && (
                <Badge variant="info" size="sm">
                  {BADGE_REPETIVEL}
                </Badge>
              )}
              <button
                type="button"
                onClick={() => setBlocos((prev) => prev.filter((x) => x.key !== b.key))}
                className="text-xs font-bold text-red dark:text-red-glow hover:text-red-deep shrink-0"
              >
                Remover bloco
              </button>
            </div>
            {b.itens.map((item, i) => (
              <ItemLinha
                key={`${i}-${item}`}
                nome={item}
                onRemove={() => patchBloco(b.key, { itens: b.itens.filter((_, j) => j !== i) })}
              />
            ))}
            <AddItemInline onAdd={(n) => patchBloco(b.key, { itens: [...b.itens, n] })} />
          </div>
        ))}
      </div>
      <AddBlocoForm
        onAdd={(n, tipoBloco) =>
          setBlocos((prev) => [...prev, { key: Date.now() + prev.length, nome: n, tipoBloco, itens: [] }])
        }
      />
    </DrawerShell>
  );
}

// ─── Drawer: EDITAR modelo (blocos/itens direto no servidor) ──────────

function EditarModeloDrawer({
  modelo,
  tipoOpcoes,
  onClose,
}: {
  modelo: OrcamentoModeloResumo;
  tipoOpcoes: TipoOpcao[];
  onClose: () => void;
}) {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const { updateModelo, removeModelo } = useModelosActions();
  const blocosQuery = useModeloBlocos(modelo.id);
  const actions = useModeloBlocoActions(modelo.id);
  const [nome, setNome] = useState(modelo.nome);
  const [tipoId, setTipoId] = useState(modelo.tipoId ?? '');

  const blocos = blocosQuery.data?.blocos ?? [];

  function salvar() {
    const n = nome.trim();
    if (!n) return;
    updateModelo.mutate(
      { modeloId: modelo.id, patch: { nome: n, tipoId: tipoId || null } },
      {
        onSuccess: () => {
          toast.show('Modelo salvo.');
          onClose();
        },
        onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível salvar o modelo.', { variant: 'error' }),
      },
    );
  }

  async function excluirModelo() {
    const ok = await confirm({
      title: 'Excluir modelo?',
      message: `Excluir o modelo "${modelo.nome}" com os blocos e itens sugeridos dele? Orçamentos já criados não mudam.`,
      confirmLabel: 'Excluir',
      variant: 'destructive',
    });
    if (!ok) return;
    removeModelo.mutate(modelo.id, {
      onSuccess: () => {
        toast.show('Modelo excluído.');
        onClose();
      },
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível excluir o modelo.', { variant: 'error' }),
    });
  }

  return (
    <DrawerShell
      titulo="Editar modelo"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark" onClick={excluirModelo}>
            Excluir modelo
          </Button>
          <span className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={salvar} disabled={!nome.trim() || updateModelo.isPending} data-testid="btn-salvar-modelo">
            {updateModelo.isPending ? 'Salvando…' : 'Salvar modelo'}
          </Button>
        </>
      }
    >
      <CamposNomeTipo nome={nome} setNome={setNome} tipoId={tipoId} setTipoId={setTipoId} tipoOpcoes={tipoOpcoes} />
      <TituloBlocos />
      {blocosQuery.isLoading ? (
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">Carregando blocos…</p>
      ) : (
        <div className="space-y-3 mb-3">
          {blocos.length === 0 && (
            <p className="text-sm text-ink-3 dark:text-ink-dark-3 italic">
              Sem blocos — quem aplicar este modelo começa com o orçamento livre.
            </p>
          )}
          {blocos.map((b) => (
            <BlocoServidorCard key={b.id} bloco={b} actions={actions} />
          ))}
        </div>
      )}
      <AddBlocoForm
        pending={actions.addBloco.isPending}
        onAdd={(n, tipoBloco) =>
          actions.addBloco.mutate(
            { nome: n, tipoBloco },
            { onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível adicionar o bloco.', { variant: 'error' }) },
          )
        }
      />
    </DrawerShell>
  );
}

function BlocoServidorCard({ bloco, actions }: { bloco: ModeloBloco; actions: ReturnType<typeof useModeloBlocoActions> }) {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const [nome, setNome] = useState(bloco.nome);

  // Re-sincroniza o nome se o servidor devolver outro (ex.: refetch).
  useEffect(() => setNome(bloco.nome), [bloco.nome]);

  function commitNome() {
    const n = nome.trim();
    if (!n || n === bloco.nome) {
      setNome(bloco.nome);
      return;
    }
    actions.updateBloco.mutate(
      { blocoId: bloco.id, patch: { nome: n } },
      { onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível renomear o bloco.', { variant: 'error' }) },
    );
  }

  async function remover() {
    const ok = await confirm({
      title: 'Remover bloco?',
      message: `Remover o bloco "${bloco.nome}" e os itens sugeridos dele? Orçamentos já criados não mudam.`,
      confirmLabel: 'Remover',
      variant: 'destructive',
    });
    if (!ok) return;
    actions.removeBloco.mutate(bloco.id, {
      onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível remover o bloco.', { variant: 'error' }),
    });
  }

  return (
    <div
      className="rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface p-3.5"
      data-testid={`bloco-${bloco.id}`}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          onBlur={commitNome}
          onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          aria-label={`Nome do bloco ${bloco.nome}`}
          className="flex-1 min-w-0 bg-transparent font-semibold text-base text-ink-1 dark:text-ink-dark-1 focus:outline-none focus:border-b focus:border-red"
        />
        {bloco.tipoBloco === 'repetivel' && (
          <Badge variant="info" size="sm">
            {BADGE_REPETIVEL}
          </Badge>
        )}
        <button
          type="button"
          onClick={remover}
          className="text-xs font-bold text-red dark:text-red-glow hover:text-red-deep shrink-0"
        >
          Remover bloco
        </button>
      </div>
      {bloco.itens.map((it) => (
        <ItemLinha
          key={it.id}
          nome={it.nome}
          onRemove={() =>
            actions.removeItem.mutate(it.id, {
              onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível remover o item.', { variant: 'error' }),
            })
          }
        />
      ))}
      <AddItemInline
        onAdd={(n) =>
          actions.addItem.mutate(
            { blocoId: bloco.id, nome: n },
            { onError: (e) => toast.show(errMsg(e) ?? 'Não foi possível adicionar o item.', { variant: 'error' }) },
          )
        }
      />
    </div>
  );
}

export default ModelosOrcamentoTab;
